import React from 'react';
import { Box, ToggleButtonGroup, ToggleButton, Typography, Stack, Tooltip } from '@mui/material';

/**
 * Reusable 1-5 (or any count) scale selector
 * Supports icons and labels for each option
 * 
 * @param {string} value - Currently selected value (index as string)
 * @param {function} onChange - Callback when selection changes (receives index as string)
 * @param {array} icons - Optional array of icons/emojis for each option
 * @param {array} labels - Optional array of labels for each option
 * @param {number} count - Number of options (default 5, supports 6 for domain question)
 * @param {boolean} vertical - Whether to display vertically (default false)
 * @param {array} tooltips - Optional array of tooltip texts for each option
 */
const ScoreScale = ({ 
  value, 
  onChange, 
  icons = [], 
  labels = [], 
  count = 5,
  vertical = false,
  tooltips = []
}) => {
  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  const options = Array.from({ length: count }, (_, i) => i);

  // Wrapper component for optional tooltips
  const OptionWrapper = ({ tooltip, children }) => {
    if (!tooltip) return children;

    return (
      <Tooltip title={tooltip} arrow placement="top">
        <Box component="span" sx={{ display: 'contents' }}>{children}</Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ width: '100%', my: 2 }}>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        orientation={vertical ? 'vertical' : 'horizontal'}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'center',
          '& .MuiToggleButton-root': {
            flex: vertical ? '1 1 100%' : { xs: '1 1 45%', sm: '1 1 auto' },
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            px: { xs: 1, sm: 2 },
            py: 1.5,
            textTransform: 'none',
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
          },
        }}
      >
        {options.map((index) => (
          <OptionWrapper key={index} tooltip={tooltips?.[index]}>
            <ToggleButton 
              value={String(index)}
              aria-label={labels[index] || `Option ${index + 1}`}
            >
              <Stack spacing={0.5} alignItems="center">
                {icons[index] && (
                  <Typography variant="h5" component="span">
                    {icons[index]}
                  </Typography>
                )}
                {labels[index] && (
                  <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                    {typeof labels[index] === 'object' ? JSON.stringify(labels[index]) : labels[index]}
                  </Typography>
                )}
                {!icons[index] && !labels[index] && (
                  <Typography variant="body1">
                    {index + 1}
                  </Typography>
                )}
              </Stack>
            </ToggleButton>
          </OptionWrapper>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default ScoreScale;
